/**
 * Network Monitor Service
 * Monitors network connectivity and provides recovery mechanisms
 * Implements Requirements: 2.4, 3.1
 */

import { errorHandlingService, ErrorType, ErrorSeverity } from './ErrorHandlingService';
import { notificationService } from './NotificationService';

export enum NetworkStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
  SLOW = 'slow',
  UNSTABLE = 'unstable'
}

export interface NetworkState {
  status: NetworkStatus;
  isOnline: boolean;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  lastChecked: Date;
}

export interface NetworkRetryOptions {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  timeout?: number;
}

type NetworkStatusListener = (state: NetworkState) => void;

export class NetworkMonitorService {
  private static instance: NetworkMonitorService;
  private listeners: NetworkStatusListener[] = [];
  private currentState: NetworkState;
  private checkInterval: number | null = null;
  private retryQueue: Map<string, () => Promise<any>> = new Map();

  private constructor() {
    this.currentState = this.getCurrentNetworkState();
    this.setupNetworkMonitoring();
  }

  static getInstance(): NetworkMonitorService {
    if (!NetworkMonitorService.instance) {
      NetworkMonitorService.instance = new NetworkMonitorService();
    }
    return NetworkMonitorService.instance;
  }

  /**
   * Get current network state
   */
  getNetworkState(): NetworkState {
    return { ...this.currentState };
  }

  /**
   * Check if currently online
   */
  isOnline(): boolean {
    return this.currentState.isOnline;
  }

  /**
   * Check if network is slow or unstable
   */
  isNetworkSlow(): boolean {
    return this.currentState.status === NetworkStatus.SLOW || 
           this.currentState.status === NetworkStatus.UNSTABLE;
  }

  /**
   * Subscribe to network status changes
   */
  subscribe(listener: NetworkStatusListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index !== -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Perform network request with automatic retry and error handling
   */
  async performNetworkRequest<T>(
    requestFn: () => Promise<T>,
    options: NetworkRetryOptions = {},
    context?: { action?: string; component?: string }
  ): Promise<T> {
    const defaultOptions: Required<NetworkRetryOptions> = {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 10000,
      backoffFactor: 2,
      timeout: 30000
    };

    const finalOptions = { ...defaultOptions, ...options };
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= finalOptions.maxAttempts; attempt++) {
      try {
        // Check network status before attempting
        if (!this.isOnline() && attempt === 1) {
          throw new Error('Network is offline');
        }

        // Create timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), finalOptions.timeout);
        });

        // Race between request and timeout
        const result = await Promise.race([
          requestFn(),
          timeoutPromise
        ]);

        // Success - clear any queued retries for this request
        if (context?.action) {
          this.retryQueue.delete(context.action);
        }

        return result;

      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        // Don't retry on the last attempt
        if (attempt === finalOptions.maxAttempts) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          finalOptions.baseDelay * Math.pow(finalOptions.backoffFactor, attempt - 1),
          finalOptions.maxDelay
        );

        // Log retry attempt
        console.warn(`Network request failed (attempt ${attempt}/${finalOptions.maxAttempts}), retrying in ${delay}ms:`, error);

        // Wait before retry
        await this.delay(delay);

        // Check if we should continue retrying
        if (!this.shouldRetry(lastError, attempt)) {
          break;
        }
      }
    }

    // All attempts failed - handle the error
    const errorType = this.getErrorType(lastError!);
    const errorSeverity = this.getErrorSeverity(lastError!, finalOptions.maxAttempts);

    // Queue for retry when network recovers if it's a network error
    if (errorType === ErrorType.NETWORK && context?.action) {
      this.queueForRetry(context.action, () => this.performNetworkRequest(requestFn, options, context));
    }

    // Handle the error through error handling service
    errorHandlingService.handleError(
      lastError!,
      errorType,
      errorSeverity,
      {
        action: context?.action,
        component: context?.component,
        additionalData: {
          attempts: finalOptions.maxAttempts,
          networkStatus: this.currentState.status
        }
      },
      {
        showNotification: true,
        reportError: errorSeverity === ErrorSeverity.HIGH
      }
    );

    throw lastError;
  }

  /**
   * Test network connectivity
   */
  async testConnectivity(): Promise<boolean> {
    try {
      // Try to fetch a small resource with cache-busting
      const response = await fetch('/favicon.ico?t=' + Date.now(), {
        method: 'HEAD',
        cache: 'no-cache'
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Get network quality metrics
   */
  getNetworkQuality(): { quality: 'good' | 'fair' | 'poor'; metrics: any } {
    const connection = (navigator as any).connection;
    
    if (!connection) {
      return { quality: 'good', metrics: {} };
    }

    const { effectiveType, downlink, rtt } = connection;
    
    let quality: 'good' | 'fair' | 'poor' = 'good';
    
    if (effectiveType === 'slow-2g' || effectiveType === '2g' || rtt > 2000) {
      quality = 'poor';
    } else if (effectiveType === '3g' || rtt > 1000 || downlink < 1) {
      quality = 'fair';
    }

    return {
      quality,
      metrics: {
        effectiveType,
        downlink,
        rtt,
        saveData: connection.saveData
      }
    };
  }

  /**
   * Start monitoring network status
   */
  startMonitoring(intervalMs: number = 30000): void {
    if (this.checkInterval) {
      this.stopMonitoring();
    }

    this.checkInterval = window.setInterval(() => {
      this.updateNetworkState();
    }, intervalMs);
  }

  /**
   * Stop monitoring network status
   */
  stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Process queued retries when network recovers
   */
  private async processRetryQueue(): Promise<void> {
    if (this.retryQueue.size === 0) {
      return;
    }

    console.log(`Processing ${this.retryQueue.size} queued network requests`);
    
    const retries = Array.from(this.retryQueue.entries());
    this.retryQueue.clear();

    // Process retries with some delay between them
    for (const [action, retryFn] of retries) {
      try {
        await retryFn();
        console.log(`Successfully retried action: ${action}`);
      } catch (error) {
        console.warn(`Retry failed for action: ${action}`, error);
      }
      
      // Small delay between retries to avoid overwhelming the network
      await this.delay(500);
    }
  }

  /**
   * Queue a request for retry when network recovers
   */
  private queueForRetry(action: string, retryFn: () => Promise<any>): void {
    this.retryQueue.set(action, retryFn);
    console.log(`Queued action for retry when network recovers: ${action}`);
  }

  /**
   * Setup network monitoring event listeners
   */
  private setupNetworkMonitoring(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.updateNetworkState();
      this.processRetryQueue();
      notificationService.sendSuccessNotification('Connection restored');
    });

    window.addEventListener('offline', () => {
      this.updateNetworkState();
    });

    // Listen for connection changes (if supported)
    const connection = (navigator as any).connection;
    if (connection) {
      connection.addEventListener('change', () => {
        this.updateNetworkState();
      });
    }

    // Start periodic monitoring
    this.startMonitoring();
  }

  /**
   * Update current network state
   */
  private updateNetworkState(): void {
    const newState = this.getCurrentNetworkState();
    const statusChanged = newState.status !== this.currentState.status;
    
    this.currentState = newState;

    if (statusChanged) {
      this.notifyListeners();
    }
  }

  /**
   * Get current network state from browser APIs
   */
  private getCurrentNetworkState(): NetworkState {
    const isOnline = navigator.onLine;
    const connection = (navigator as any).connection;
    
    let status = isOnline ? NetworkStatus.ONLINE : NetworkStatus.OFFLINE;
    
    if (isOnline && connection) {
      const { effectiveType, rtt } = connection;
      
      if (effectiveType === 'slow-2g' || effectiveType === '2g' || rtt > 2000) {
        status = NetworkStatus.SLOW;
      } else if (rtt > 1000) {
        status = NetworkStatus.UNSTABLE;
      }
    }

    return {
      status,
      isOnline,
      effectiveType: connection?.effectiveType,
      downlink: connection?.downlink,
      rtt: connection?.rtt,
      lastChecked: new Date()
    };
  }

  /**
   * Determine if we should retry based on error type
   */
  private shouldRetry(error: Error, attempt: number): boolean {
    const errorMessage = error.message.toLowerCase();
    
    // Don't retry certain types of errors
    if (errorMessage.includes('unauthorized') || 
        errorMessage.includes('forbidden') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('bad request')) {
      return false;
    }

    // Always retry network-related errors
    if (errorMessage.includes('network') ||
        errorMessage.includes('timeout') ||
        errorMessage.includes('connection') ||
        errorMessage.includes('offline')) {
      return true;
    }

    // Retry server errors (5xx)
    if (errorMessage.includes('server error') ||
        errorMessage.includes('internal server error') ||
        errorMessage.includes('service unavailable')) {
      return true;
    }

    return false;
  }

  /**
   * Determine error type based on error message
   */
  private getErrorType(error: Error): ErrorType {
    const errorMessage = error.message.toLowerCase();
    
    if (errorMessage.includes('network') ||
        errorMessage.includes('offline') ||
        errorMessage.includes('connection')) {
      return ErrorType.NETWORK;
    }
    
    if (errorMessage.includes('timeout')) {
      return ErrorType.TIMEOUT;
    }
    
    if (errorMessage.includes('unauthorized') ||
        errorMessage.includes('forbidden')) {
      return ErrorType.AUTHENTICATION;
    }
    
    return ErrorType.SYSTEM;
  }

  /**
   * Determine error severity based on error and retry attempts
   */
  private getErrorSeverity(error: Error, maxAttempts: number): ErrorSeverity {
    const errorMessage = error.message.toLowerCase();
    
    // Critical errors that prevent core functionality
    if (errorMessage.includes('critical') ||
        errorMessage.includes('fatal')) {
      return ErrorSeverity.CRITICAL;
    }
    
    // High severity for repeated failures
    if (maxAttempts >= 3) {
      return ErrorSeverity.HIGH;
    }
    
    // Network errors are usually medium severity
    if (errorMessage.includes('network') ||
        errorMessage.includes('timeout')) {
      return ErrorSeverity.MEDIUM;
    }
    
    return ErrorSeverity.LOW;
  }

  /**
   * Notify all listeners of network state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener({ ...this.currentState });
      } catch (error) {
        console.error('Error notifying network status listener:', error);
      }
    });
  }

  /**
   * Utility function for delays
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const networkMonitorService = NetworkMonitorService.getInstance();